import * as THREE from 'three';
import { SupportResult, TrackSupportPrimitive } from '../core/supports/types';
import {
  footerPadHalfSide,
  FOOTER_MATCH_M,
  applyFooterPrintGeom,
  applyMaxBeamLengthSplits,
  BeamSplice,
} from '../core/print/supportPrims';
import {
  footerPlateSecant,
  hssBoltOrigins,
  resolveHssEndplateGeom,
} from '../core/print/endplate';
import { DEFAULT_PRINT_SETTINGS, PrintSettings } from '../core/print/types';

const STRATEGY_COLORS: Record<string, number> = {
  vertical: 0x9aa5b1,
  leaning: 0x7fb2d9,
  aframe: 0xd9b97f,
  knee: 0xc98fd9,
  forced: 0xd97f7f,
};

/** Steel structure renderer: merged cylinders per primitive, colour-codable by placement strategy. */
export class SupportMesh {
  readonly group = new THREE.Group();
  debugColors = false;
  private meshes: THREE.Mesh[] = [];
  private steelMat = new THREE.MeshStandardMaterial({ color: 0xb9c0c9, roughness: 0.45, metalness: 0.6 });
  private footerMat = new THREE.MeshStandardMaterial({ color: 0x9d9d94, roughness: 0.9, metalness: 0.05 });

  constructor() {
    this.group.name = 'supports';
  }

  rebuild(result: SupportResult, printSettings: PrintSettings = DEFAULT_PRINT_SETTINGS): void {
    for (const m of this.meshes) {
      this.group.remove(m);
      m.geometry.dispose();
      if (Array.isArray(m.material)) m.material.forEach((mm) => mm.dispose());
      else if (m.material !== this.steelMat && m.material !== this.footerMat) (m.material as THREE.Material).dispose();
    }
    this.meshes = [];

    const byMaterial = new Map<number | 'steel' | 'footer', THREE.BufferGeometry[]>();
    const push = (key: number | 'steel' | 'footer', geo: THREE.BufferGeometry) => {
      const arr = byMaterial.get(key) ?? [];
      arr.push(geo);
      byMaterial.set(key, arr);
    };

    const footered = applyFooterPrintGeom(result.primitives, printSettings);
    const { prims, splices } = applyMaxBeamLengthSplits(footered, printSettings);

    for (const prim of prims) {
      if (prim.kind === 'footer') {
        const half = footerPadHalfSide(prim.radius);
        const geo = new THREE.BoxGeometry(half * 2, prim.height, half * 2);
        geo.translate(prim.pos.x, prim.pos.y + prim.height / 2, prim.pos.z);
        push('footer', geo);
      } else if (prim.kind === 'joint') {
        const geo = new THREE.SphereGeometry(prim.radius, 12, 10);
        geo.translate(prim.pos.x, prim.pos.y, prim.pos.z);
        push('steel', geo);
      } else {
        const geo = tubeBetween(prim);
        if (this.debugColors && prim.kind === 'column') {
          push(STRATEGY_COLORS[prim.strategy] ?? 0xffffff, geo);
        } else {
          push('steel', geo);
        }
      }
    }

    if (printSettings.hssEndplateEnabled) {
      addFooterEndplates(prims, printSettings, (g) => push('steel', g));
      addBeamSpliceEndplates(splices, printSettings, (g) => push('steel', g));
    }

    for (const [key, geos] of byMaterial) {
      const merged = mergeGeometries(geos);
      const mat =
        key === 'steel'
          ? this.steelMat
          : key === 'footer'
            ? this.footerMat
            : new THREE.MeshStandardMaterial({ color: key as number, roughness: 0.45, metalness: 0.6 });
      const mesh = new THREE.Mesh(merged, mat);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      this.group.add(mesh);
      this.meshes.push(mesh);
      for (const g of geos) g.dispose();
    }
  }
}

function tubeBetween(
  prim: Extract<TrackSupportPrimitive, { kind: 'column' | 'brace' | 'knee' | 'stub' }>,
): THREE.BufferGeometry {
  const dir = new THREE.Vector3().subVectors(prim.b, prim.a);
  const len = Math.max(dir.length(), 1e-6);
  const radiusA = prim.kind === 'stub' ? prim.radiusA : prim.radius;
  const radiusB = prim.kind === 'stub' ? prim.radiusB : prim.radius;
  // CylinderGeometry(radiusTop, radiusBottom): after +Y translate, bottom sits at `a`, top at `b`.
  const geo = new THREE.CylinderGeometry(radiusB, radiusA, len, prim.kind === 'column' ? 10 : 8);
  geo.translate(0, len / 2, 0);
  const quat = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.normalize());
  geo.applyQuaternion(quat);
  geo.translate(prim.a.x, prim.a.y, prim.a.z);
  return geo;
}

function addFooterEndplates(
  prims: TrackSupportPrimitive[],
  settings: PrintSettings,
  push: (geo: THREE.BufferGeometry) => void,
): void {
  const footers = prims.filter((p) => p.kind === 'footer');
  const members = prims.filter(
    (p): p is Extract<TrackSupportPrimitive, { kind: 'column' | 'brace' | 'knee' | 'stub' }> =>
      p.kind === 'column' || p.kind === 'brace' || p.kind === 'knee' || p.kind === 'stub',
  );
  for (const foot of footers) {
    let bestR = 0;
    let bestSecant = 1;
    for (const prim of members) {
      const radiusA = prim.kind === 'stub' ? prim.radiusA : prim.radius;
      const radiusB = prim.kind === 'stub' ? prim.radiusB : prim.radius;
      const ends: { pos: THREE.Vector3; other: THREE.Vector3; radius: number }[] = [
        { pos: prim.a, other: prim.b, radius: radiusA },
        { pos: prim.b, other: prim.a, radius: radiusB },
      ];
      for (const end of ends) {
        if (!pointOnSquareFooter(end.pos, foot)) continue;
        const along = new THREE.Vector3().subVectors(end.other, end.pos);
        const secant = footerPlateSecant(along.lengthSq() > 1e-12 ? along.normalize().y : 1);
        if (end.radius > bestR) {
          bestR = end.radius;
          bestSecant = secant;
        }
      }
    }
    const geom = resolveHssEndplateGeom(bestR, settings, {
      tubeSecant: bestSecant,
      maxThickness: Math.max(foot.height, 0.12) * 0.85,
    });
    if (!geom || bestR <= 0) continue;
    const topY = foot.pos.y + Math.max(foot.height, 0.12);
    const plate = new THREE.CylinderGeometry(geom.outerRadius, geom.outerRadius, geom.thickness, 20);
    plate.translate(foot.pos.x, topY + geom.thickness * 0.5, foot.pos.z);
    push(plate);
    if (geom.headRadius > 1e-6 && geom.headHeight > 1e-6) {
      const origin = new THREE.Vector3(foot.pos.x, topY, foot.pos.z);
      for (const bolt of hssBoltOrigins(origin, new THREE.Vector3(0, 1, 0), geom)) {
        const head = new THREE.CylinderGeometry(geom.headRadius, geom.headRadius, geom.headHeight, 6);
        head.translate(bolt.x, topY + geom.thickness + geom.headHeight * 0.5, bolt.z);
        push(head);
      }
    }
  }
}

/** Round HSS flanges with bolt heads on both sides at max-beam-length splices. */
function addBeamSpliceEndplates(
  splices: BeamSplice[],
  settings: PrintSettings,
  push: (geo: THREE.BufferGeometry) => void,
): void {
  const yAxis = new THREE.Vector3(0, 1, 0);
  for (const splice of splices) {
    const geom = resolveHssEndplateGeom(splice.radius, settings, { beamSplit: true });
    if (!geom) continue;
    const dir = splice.dir.clone().normalize();
    if (dir.lengthSq() < 1e-12) continue;
    // Both flanges get bolt heads so the splice reads as bolted from either side.
    pushOrientedPlate(splice.pos, dir.clone().multiplyScalar(-1), geom, true, yAxis, push);
    pushOrientedPlate(splice.pos, dir, geom, true, yAxis, push);
  }
}

function pushOrientedPlate(
  origin: THREE.Vector3,
  intoPart: THREE.Vector3,
  geom: NonNullable<ReturnType<typeof resolveHssEndplateGeom>>,
  boltHeads: boolean,
  yAxis: THREE.Vector3,
  push: (geo: THREE.BufferGeometry) => void,
): void {
  const dir = intoPart.clone().normalize();
  const quat = new THREE.Quaternion().setFromUnitVectors(yAxis, dir);
  const plate = new THREE.CylinderGeometry(geom.outerRadius, geom.outerRadius, geom.thickness, 20);
  plate.translate(0, geom.thickness * 0.5, 0);
  plate.applyQuaternion(quat);
  plate.translate(origin.x, origin.y, origin.z);
  push(plate);
  if (!boltHeads || geom.headRadius <= 1e-6 || geom.headHeight <= 1e-6) return;
  for (const bolt of hssBoltOrigins(origin, dir, geom)) {
    const head = new THREE.CylinderGeometry(geom.headRadius, geom.headRadius, geom.headHeight, 6);
    head.translate(0, geom.thickness + geom.headHeight * 0.5, 0);
    head.applyQuaternion(quat);
    head.translate(bolt.x, bolt.y, bolt.z);
    push(head);
  }
}

function pointOnSquareFooter(
  pos: THREE.Vector3,
  foot: Extract<TrackSupportPrimitive, { kind: 'footer' }>,
): boolean {
  const half = Math.max(footerPadHalfSide(foot.radius), FOOTER_MATCH_M);
  const horiz = Math.max(Math.abs(pos.x - foot.pos.x), Math.abs(pos.z - foot.pos.z));
  if (horiz > half) return false;
  const top = foot.pos.y + Math.max(foot.height, 0.12);
  return pos.y <= top + 1e-3 && pos.y >= foot.pos.y - FOOTER_MATCH_M;
}

/** Minimal local merge (positions+normals+index) — avoids the examples util import. */
function mergeGeometries(geos: THREE.BufferGeometry[]): THREE.BufferGeometry {
  let vCount = 0;
  let iCount = 0;
  for (const g of geos) {
    vCount += g.attributes.position.count;
    iCount += g.index ? g.index.count : g.attributes.position.count;
  }
  const positions = new Float32Array(vCount * 3);
  const normals = new Float32Array(vCount * 3);
  const indices = new Uint32Array(iCount);
  let vOff = 0;
  let iOff = 0;
  for (const g of geos) {
    positions.set(g.attributes.position.array as Float32Array, vOff * 3);
    normals.set(g.attributes.normal.array as Float32Array, vOff * 3);
    const idx = g.index;
    const n = g.attributes.position.count;
    if (idx) {
      for (let i = 0; i < idx.count; i++) indices[iOff + i] = idx.getX(i) + vOff;
      iOff += idx.count;
    } else {
      for (let i = 0; i < n; i++) indices[iOff + i] = i + vOff;
      iOff += n;
    }
    vOff += n;
  }
  const out = new THREE.BufferGeometry();
  out.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  out.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
  out.setIndex(new THREE.BufferAttribute(indices, 1));
  return out;
}
